function responseError(code) {
  const error = new TypeError(code);
  error.code = code;
  return error;
}

export function createDashboardClipboard({
  navigatorRef = globalThis.navigator,
  documentRef = globalThis.document,
} = {}) {
  if (typeof navigatorRef?.clipboard?.writeText === 'function') {
    return Object.freeze({
      writeText(value) {
        return navigatorRef.clipboard.writeText(String(value));
      },
    });
  }
  if (!documentRef?.body || typeof documentRef.createElement !== 'function'
    || typeof documentRef.execCommand !== 'function') return null;
  return Object.freeze({
    async writeText(value) {
      const textarea = documentRef.createElement('textarea');
      textarea.value = String(value);
      textarea.setAttribute('readonly', '');
      Object.assign(textarea.style, {
        position: 'fixed', inset: '-1000px auto auto -1000px', opacity: '0',
      });
      documentRef.body.appendChild(textarea);
      try {
        textarea.focus();
        textarea.select();
        if (documentRef.execCommand('copy') !== true) {
          throw responseError('BULK_COPY_CLIPBOARD_FAILED');
        }
      } finally {
        documentRef.body.removeChild(textarea);
      }
    },
  });
}

/**
 * Copies the container numbers the table is showing, in the order it is showing
 * them.
 *
 * This used to ask the server for them, and the server answers in document-id
 * order - so picking "the first 10" handed over ten numbers from nowhere in
 * particular rather than the ten rows under the operator's eyes (Alan,
 * 2026-08-08: "复制的根本不是从我看到现在的表格从上到下的"). The rows are already
 * on the client, already sorted into the approved order, so the list is right
 * there and no request is needed at all.
 */
export function createDashboardBulkCopyController({
  clipboard,
  onState,
  numbers = () => [],
  maxItems = 5000,
}) {
  if (typeof clipboard?.writeText !== 'function' || typeof onState !== 'function'
    || typeof numbers !== 'function'
    || !Number.isSafeInteger(maxItems) || maxItems < 1) {
    throw new TypeError('BULK_COPY_DEPENDENCY_INVALID');
  }

  let generation = 0;

  function emit(status, detail = {}) {
    onState({ status, ...detail });
  }

  /** The displayed numbers, cleaned: blanks dropped, duplicates dropped, order kept. */
  function displayed() {
    const seen = new Set();
    const list = [];
    for (const value of numbers() ?? []) {
      const number = typeof value === 'string' ? value.trim() : '';
      if (!number || seen.has(number)) continue;
      seen.add(number);
      list.push(number);
    }
    return list;
  }

  /**
   * Copy one group of `size`, counting groups from zero and from the top of the
   * table. A null size is everything on screen, which is what All asks for.
   */
  async function copyGroup(_query, { size = null, index = 0 } = {}) {
    const token = ++generation;
    try {
      const list = displayed();
      if (list.length === 0) {
        emit('empty', { count: 0 });
        return { count: 0, copied: false, reason: 'empty' };
      }
      if (list.length > maxItems) {
        emit('limit', { count: list.length, maxItems });
        return { count: list.length, copied: false, reason: 'limit' };
      }
      const perGroup = Number.isSafeInteger(size) && size > 0 ? size : list.length;
      const start = Math.max(0, Math.floor(index)) * perGroup;
      const slice = list.slice(start, start + perGroup);
      if (slice.length === 0) {
        emit('empty', { count: 0 });
        return { count: 0, copied: false, reason: 'empty' };
      }
      await clipboard.writeText(slice.join('\n'));
      if (token !== generation) return { count: 0, copied: false, reason: 'cancelled' };
      emit('success', { count: slice.length });
      // The numbers travel back with the result so the table can mark the rows that
      // just went to the clipboard (Alan, 2026-08-09: copying is a lookup, and you
      // need to see which ones you already looked up).
      return { count: slice.length, copied: true, total: list.length, numbers: slice };
    } catch (error) {
      if (token !== generation) return { count: 0, copied: false, reason: 'cancelled' };
      emit('error', { error });
      throw error;
    }
  }

  /**
   * The numbers one group holds, without touching the clipboard.
   *
   * Same slicing as copyGroup so the highlight can never disagree with what the
   * copy would take - two implementations of "which group is this" is exactly
   * the bug this avoids.
   */
  function groupNumbers({ size = null, index = 0 } = {}) {
    const list = displayed();
    if (list.length === 0 || list.length > maxItems) return [];
    const perGroup = Number.isSafeInteger(size) && size > 0 ? size : list.length;
    const start = Math.max(0, Math.floor(index)) * perGroup;
    return list.slice(start, start + perGroup);
  }

  return {
    copyGroup,
    groupNumbers,
    /** Every number on screen, in one go. */
    copy(query) {
      return copyGroup(query, { size: null, index: 0 });
    },
    /** How many are on screen, for the panel's group arithmetic. */
    available() {
      return displayed().length;
    },
    cancel() {
      generation += 1;
      emit('idle');
    },
  };
}
